import { legacy } from '@/shared/api/client';
import {
  approveCampaign,
  deleteCampaign,
  getCampaign,
  listAudiences,
  listCampaigns,
  mapAudienceOption,
  marketingKeys,
  passbookHeaders,
  runCampaignAction,
  unwrap,
  type ApiCampaignAction,
  type ApiEnvelope,
  type AudienceSegment,
} from '@/features/marketing-shared';
import type { SmsAccountNumber, SmsCampaign, SmsDraftInput } from '../model/types';
import { mapSmsCampaign, mapSmsSender, toSmsApproveBody, type ApiSmsCampaign, type ApiSmsSender } from './sms-mappers';

/**
 * `/v1/sms-marketing/*` do fixe-api, via `/bff/api`. Toda função recebe a loja
 * e manda `X-Passbook-Id`: o BFF confere a sessão e a API confere papel, loja
 * e a flag `hasSmsMarketing`.
 */

const CHANNEL = 'sms' as const;
const BASE = '/sms-marketing';

export const smsMarketingKeys = {
  campaigns: (idPassbook: string) => marketingKeys.campaigns(CHANNEL, idPassbook),
  campaignAll: (idPassbook: string, id: string) => marketingKeys.campaignAll(CHANNEL, idPassbook, id),
  campaign: (idPassbook: string, id: string, page: number) => marketingKeys.campaign(CHANNEL, idPassbook, id, page),
  audiences: (idPassbook: string) => marketingKeys.audiences(CHANNEL, idPassbook),
  numbers: (idPassbook: string) => marketingKeys.numbers(idPassbook),
};

export async function fetchSmsCampaigns(idPassbook: string): Promise<SmsCampaign[]> {
  const rows = await listCampaigns<ApiSmsCampaign>(CHANNEL, idPassbook);
  return rows.map(mapSmsCampaign);
}

export async function fetchSmsCampaign(
  id: string,
  idPassbook: string,
  paging: { page: number; pageSize: number } = { page: 1, pageSize: 50 },
): Promise<SmsCampaign> {
  return mapSmsCampaign(await getCampaign<ApiSmsCampaign>(CHANNEL, id, idPassbook, paging));
}

export async function approveSmsCampaign(
  idPassbook: string,
  input: SmsDraftInput,
  allowPartial: boolean,
): Promise<{ campaign: SmsCampaign; leftOut: number }> {
  const result = await approveCampaign<ApiSmsCampaign>(CHANNEL, idPassbook, toSmsApproveBody(input, allowPartial));
  return { campaign: mapSmsCampaign(result.campaign), leftOut: result.leftOut };
}

export async function smsCampaignAction(action: ApiCampaignAction, id: string, idPassbook: string): Promise<SmsCampaign> {
  return mapSmsCampaign(await runCampaignAction<ApiSmsCampaign>(CHANNEL, id, action, idPassbook));
}

export async function deleteSmsCampaign(id: string, idPassbook: string): Promise<void> {
  await deleteCampaign(CHANNEL, id, idPassbook);
}

export async function fetchSmsAudiences(idPassbook: string): Promise<AudienceSegment[]> {
  const rows = await listAudiences(CHANNEL, idPassbook);
  return rows.map(mapAudienceOption);
}

/** Remetentes da conta do provedor liberados para a loja (número pausado vem, mas não é escolhível). */
export async function fetchSmsNumbers(idPassbook: string): Promise<SmsAccountNumber[]> {
  const res = await legacy.get<ApiEnvelope<ApiSmsSender[]>>(
    `${BASE}/${encodeURIComponent(idPassbook)}/numbers`,
    passbookHeaders(idPassbook),
  );
  const rows = unwrap(res);
  return Array.isArray(rows) ? rows.map(mapSmsSender) : [];
}
